import { z } from "zod";

import { pageRefSchema, provenanceTypeSchema, reviewStateSchema } from "../schema";

const nodeIdSchema = z.string().regex(/^M1S2_N0[1-7]$/);
const claimIdSchema = z.string().regex(/^M1S2_CLAIM_[A-Z0-9_]+$/);

const sourceAttributionSchema = z.object({
  sourceRefs: z.array(z.string().min(1)).min(1),
  provenanceType: provenanceTypeSchema,
  sourceCategory: z.literal("教材正文"),
  citationLabel: z.string().min(1),
  pageRefs: z.array(pageRefSchema).min(1),
});

const narrativeRenderSchema = z.object({
  kind: z.literal("narrative"),
  paragraphs: z.array(z.string().min(1)).min(1),
  emphasisTerms: z.array(z.string().min(1)),
});

const archiveDocumentRenderSchema = z.object({
  kind: z.literal("archive_document"),
  representationLabel: z.literal("教材载述文献"),
  context: z.string().min(1),
  documents: z
    .array(
      z.object({
        title: z.string().min(1),
        dateLabel: z.string().min(1),
        documentType: z.string().min(1),
        excerpt: z.string().min(1).optional(),
      }),
    )
    .min(1),
});

const routeComparisonRenderSchema = z
  .object({
    kind: z.literal("route_comparison"),
    comparisonQuestion: z.string().min(1),
    routes: z
      .array(
        z.object({
          id: z.enum(["partial", "comprehensive"]),
          title: z.string().min(1),
          actor: z.string().min(1),
          reliance: z.string().min(1),
          democracyAndLivelihood: z.string().min(1),
          massMobilization: z.string().min(1),
        }),
      )
      .length(2),
    takeaway: z.string().min(1),
  })
  .superRefine((render, context) => {
    if (new Set(render.routes.map((route) => route.id)).size !== 2) {
      context.addIssue({
        code: "custom",
        message: "两条抗战路线必须各出现一次。",
        path: ["routes"],
      });
    }
  });

const featureGridRenderSchema = z
  .object({
    kind: z.literal("feature_grid"),
    intro: z.string().min(1),
    features: z
      .array(
        z.object({
          id: z.string().regex(/^feature_[1-4]$/),
          axis: z.string().min(1),
          japan: z.string().min(1),
          china: z.string().min(1),
        }),
      )
      .length(4),
    conclusion: z.string().min(1),
  })
  .superRefine((render, context) => {
    if (new Set(render.features.map((feature) => feature.id)).size !== 4) {
      context.addIssue({
        code: "custom",
        message: "四个基本特点的ID必须唯一。",
        path: ["features"],
      });
    }
  });

const stageFlowRenderSchema = z
  .object({
    kind: z.literal("stage_flow"),
    stages: z
      .array(
        z.object({
          id: z.enum(["defense", "stalemate", "counteroffensive"]),
          order: z.number().int().min(1).max(3),
          title: z.string().min(1),
          chinaPowerPosition: z.enum(["劣势", "平衡", "优势"]),
        }),
      )
      .length(3),
    powerTrajectory: z.literal("劣势→平衡→优势"),
    pivotStageId: z.literal("stalemate"),
    pivotExplanation: z.string().min(1),
  })
  .superRefine((render, context) => {
    if (
      new Set(render.stages.map((stage) => stage.id)).size !== 3 ||
      render.stages.some((stage, index) => stage.order !== index + 1)
    ) {
      context.addIssue({
        code: "custom",
        message: "持久战三个阶段必须唯一并按1—3顺序排列。",
        path: ["stages"],
      });
    }
  });

const nodeRenderSchema = z.discriminatedUnion("kind", [
  narrativeRenderSchema,
  archiveDocumentRenderSchema,
  routeComparisonRenderSchema,
  featureGridRenderSchema,
  stageFlowRenderSchema,
]);

export const m1s2BlueprintNodeSchema = z.object({
  id: nodeIdSchema,
  sceneId: z.literal("M1S2"),
  order: z.number().int().positive(),
  nodeType: z.enum(["lecture", "archive"]),
  title: z.string().min(1),
  timeAnchor: z.enum(["1937年8月", "1938年5—6月"]),
  estimatedSeconds: z.number().int().positive(),
  claimId: claimIdSchema,
  claim: z.string().min(1),
  assetRefs: z.array(z.string().regex(/^AST-M1S2-[A-Z0-9-]+$/)).min(1),
  source: sourceAttributionSchema,
  reviewState: reviewStateSchema,
  reviewNote: z.string().min(1).optional(),
  render: nodeRenderSchema,
});

const reviewNoteSchema = z.object({
  id: z.string().regex(/^M1S2_REVIEW_[A-Z0-9_]+$/),
  scope: z.string().min(1),
  priority: z.enum(["before_frontend_release", "before_formal_release"]),
  state: z.literal("open"),
  note: z.string().min(1),
});

export const m1s2BlueprintSchema = z
  .object({
    id: z.literal("M1S2"),
    version: z.literal("1.0.0"),
    title: z.literal("党的全面抗战路线和持久战的战略总方针"),
    identity: z.literal("抗战史料研学员"),
    timeNodes: z.tuple([z.literal("1937年8月"), z.literal("1938年5—6月")]),
    mainVenues: z.tuple([z.literal("陕北洛川城郊"), z.literal("延安《论持久战》讲演场景")]),
    sourceRefs: z.tuple([z.literal("SRC_TEXTBOOK_CH3")]),
    taskIds: z.tuple([z.literal("T02"), z.literal("T03"), z.literal("T04")]),
    sequence: z.array(z.string().regex(/^(M1S2_N0[1-7]|T0[2-4])$/)).length(10),
    nodes: z.array(m1s2BlueprintNodeSchema).length(7),
    reviewState: reviewStateSchema,
    reviewNotes: z.array(reviewNoteSchema).min(1),
  })
  .superRefine((blueprint, context) => {
    const nodeIds = blueprint.nodes.map((node) => node.id);
    const claimIds = blueprint.nodes.map((node) => node.claimId);
    const orders = blueprint.nodes.map((node) => node.order);
    const expectedSequenceItems = [...nodeIds, ...blueprint.taskIds];

    if (new Set(nodeIds).size !== nodeIds.length) {
      context.addIssue({ code: "custom", message: "M1S2节点ID必须唯一。", path: ["nodes"] });
    }
    if (new Set(claimIds).size !== claimIds.length) {
      context.addIssue({ code: "custom", message: "M1S2 claimId必须唯一。", path: ["nodes"] });
    }
    if (new Set(orders).size !== orders.length) {
      context.addIssue({ code: "custom", message: "M1S2节点顺序必须唯一。", path: ["nodes"] });
    }
    if (orders.some((order, index) => order !== (index + 1) * 10)) {
      context.addIssue({
        code: "custom",
        message: "M1S2节点必须按10—70的渲染顺序声明。",
        path: ["nodes"],
      });
    }
    if (blueprint.nodes.reduce((total, node) => total + node.estimatedSeconds, 0) !== 300) {
      context.addIssue({
        code: "custom",
        message: "M1S2讲授与史料节点总时长必须保持约5分钟（300秒）。",
        path: ["nodes"],
      });
    }
    for (const [index, node] of blueprint.nodes.entries()) {
      const isArchiveDocument = node.render.kind === "archive_document";
      if ((node.nodeType === "archive") !== isArchiveDocument) {
        context.addIssue({
          code: "custom",
          message: `${node.id}的nodeType与render.kind不一致。`,
          path: ["nodes", index],
        });
      }
      if (
        node.source.provenanceType !== "textbook" ||
        node.source.sourceRefs.length !== 1 ||
        node.source.sourceRefs[0] !== "SRC_TEXTBOOK_CH3"
      ) {
        context.addIssue({
          code: "custom",
          message: `${node.id}必须明确归属教材第三章来源。`,
          path: ["nodes", index, "source"],
        });
      }
    }
    if (
      new Set(blueprint.sequence).size !== blueprint.sequence.length ||
      expectedSequenceItems.some((item) => !blueprint.sequence.includes(item))
    ) {
      context.addIssue({
        code: "custom",
        message: "M1S2渲染序列必须且只能完整包含7个内容节点与任务T02—T04。",
        path: ["sequence"],
      });
    }
  });

export type M1S2BlueprintNode = z.infer<typeof m1s2BlueprintNodeSchema>;
export type M1S2Blueprint = z.infer<typeof m1s2BlueprintSchema>;

const textbookSource = (print: string, pdf: string): M1S2BlueprintNode["source"] => ({
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  provenanceType: "textbook",
  sourceCategory: "教材正文",
  citationLabel: "《中国共产党简史》第三章",
  pageRefs: [{ print, pdf }],
});

export const m1s2Blueprint = {
  id: "M1S2",
  version: "1.0.0",
  title: "党的全面抗战路线和持久战的战略总方针",
  identity: "抗战史料研学员",
  timeNodes: ["1937年8月", "1938年5—6月"],
  mainVenues: ["陕北洛川城郊", "延安《论持久战》讲演场景"],
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  taskIds: ["T02", "T03", "T04"],
  sequence: [
    "M1S2_N01",
    "M1S2_N02",
    "M1S2_N03",
    "T02",
    "M1S2_N04",
    "M1S2_N05",
    "M1S2_N06",
    "M1S2_N07",
    "T03",
    "T04",
  ],
  nodes: [
    {
      id: "M1S2_N01",
      sceneId: "M1S2",
      order: 10,
      nodeType: "archive",
      title: "洛川会议｜全面抗战路线的文献起点",
      timeAnchor: "1937年8月",
      estimatedSeconds: 40,
      claimId: "M1S2_CLAIM_LUOCHUAN_DOCUMENTS",
      claim:
        "1937年8月，中共中央在陕北洛川城郊召开政治局扩大会议，通过抗日救国十大纲领和相关宣传鼓动提纲。",
      assetRefs: ["AST-M1S2-CONFERENCE-TABLE"],
      source: textbookSource("75—76", "95—96"),
      reviewState: "pending_review",
      render: {
        kind: "archive_document",
        representationLabel: "教材载述文献",
        context:
          "1937年8月，中共中央在陕北洛川城郊召开政治局扩大会议（洛川会议）。这是在全国抗战刚刚爆发的历史转折关头召开的一次重要会议。",
        documents: [
          {
            title: "《中国共产党抗日救国十大纲领》",
            dateLabel: "1937年8月",
            documentType: "会议通过文件",
          },
          {
            title: "《为动员一切力量争取抗战胜利而斗争》",
            dateLabel: "1937年8月",
            documentType: "毛泽东起草的宣传鼓动提纲",
          },
        ],
      },
    },
    {
      id: "M1S2_N02",
      sceneId: "M1S2",
      order: 20,
      nodeType: "lecture",
      title: "全面抗战路线正式形成",
      timeAnchor: "1937年8月",
      estimatedSeconds: 50,
      claimId: "M1S2_CLAIM_COMPREHENSIVE_ROUTE",
      claim: "洛川会议通过的十大纲领和决定，标志着党的全面抗战路线正式形成。",
      assetRefs: ["AST-M1S2-CONFERENCE-TABLE"],
      source: textbookSource("75—76", "95—96"),
      reviewState: "pending_review",
      render: {
        kind: "narrative",
        paragraphs: [
          "会议强调，必须坚持统一战线中无产阶级的领导权，在敌人后方放手发动独立自主的山地游击战争，在国民党统治区放手发动抗日的群众运动。",
          "洛川会议通过的十大纲领和决定，标志着党的全面抗战路线的正式形成。",
        ],
        emphasisTerms: ["统一战线中无产阶级的领导权", "独立自主的山地游击战争", "抗日的群众运动"],
      },
    },
    {
      id: "M1S2_N03",
      sceneId: "M1S2",
      order: 30,
      nodeType: "lecture",
      title: "片面抗战路线与全面抗战路线比较",
      timeAnchor: "1937年8月",
      estimatedSeconds: 45,
      claimId: "M1S2_CLAIM_TWO_ROUTES",
      claim: "国共两党在如何抗日的问题上，一开始就存在着两条不同路线。",
      assetRefs: ["AST-M1S2-CONFERENCE-TABLE"],
      source: textbookSource("75", "95"),
      reviewState: "pending_review",
      render: {
        kind: "route_comparison",
        comparisonQuestion: "抗战应当依靠谁、怎样把抗战变成人民战争？",
        routes: [
          {
            id: "partial",
            title: "片面抗战路线",
            actor: "蒋介石集团",
            reliance: "单纯依靠政府和军队的抗战",
            democracyAndLivelihood: "不愿意实行民主、改善民生",
            massMobilization: "不敢发动和依靠人民大众",
          },
          {
            id: "comprehensive",
            title: "全面抗战路线",
            actor: "中国共产党",
            reliance: "充分动员、组织和武装民众抗战",
            democracyAndLivelihood:
              "废除国民党的一党专政，给人民以充分的抗日民主权利，适当地改善工农大众的生活",
            massMobilization: "使抗日战争成为真正的人民战争",
          },
        ],
        takeaway: "全面抗战路线把人民大众作为抗战最深厚的力量基础。",
      },
    },
    {
      id: "M1S2_N04",
      sceneId: "M1S2",
      order: 40,
      nodeType: "archive",
      title: "《论持久战》｜回应亡国论与速胜论",
      timeAnchor: "1938年5—6月",
      estimatedSeconds: 45,
      claimId: "M1S2_CLAIM_PROTRACTED_WAR_JUDGMENT",
      claim: "中国不会亡、不能速胜，抗日战争是持久战。",
      assetRefs: ["AST-M1S2-DOCUMENT-WALL"],
      source: textbookSource("76", "96"),
      reviewState: "pending_review",
      render: {
        kind: "archive_document",
        representationLabel: "教材载述文献",
        context:
          "为初步总结全国抗战经验，批驳当时流行的“亡国论”和“速胜论”，系统阐明党的抗日持久战方针，毛泽东在1938年五六月间作了《论持久战》的长篇讲演。",
        documents: [
          {
            title: "《论持久战》",
            dateLabel: "1938年5—6月",
            documentType: "长篇讲演",
            excerpt:
              "“中国会亡吗？答复：不会亡，最后胜利是中国的。中国能够速胜吗？答复：不能速胜，抗日战争是持久战。”",
          },
        ],
      },
    },
    {
      id: "M1S2_N05",
      sceneId: "M1S2",
      order: 50,
      nodeType: "lecture",
      title: "中日战争双方的四个基本特点",
      timeAnchor: "1938年5—6月",
      estimatedSeconds: 50,
      claimId: "M1S2_CLAIM_FOUR_FEATURES",
      claim: "四个互相矛盾的基本特点共同决定中国不能速胜，也不会亡国，最后胜利属于中国。",
      assetRefs: ["AST-M1S2-MAP-TABLE"],
      source: textbookSource("76", "96"),
      reviewState: "pending_review",
      render: {
        kind: "feature_grid",
        intro: "《论持久战》从国力、战争性质、国家规模与国际支持四个维度分析战争双方。",
        features: [
          {
            id: "feature_1",
            axis: "国力",
            japan: "帝国主义强国",
            china: "半殖民地半封建弱国",
          },
          {
            id: "feature_2",
            axis: "战争性质",
            japan: "侵略战争是退步的、野蛮的",
            china: "反侵略战争是进步的、正义的",
          },
          {
            id: "feature_3",
            axis: "国家规模与持久能力",
            japan: "小国，经不起长期战争",
            china: "大国，能够支持长期战争",
          },
          {
            id: "feature_4",
            axis: "国际道义与支持",
            japan: "非正义战争失道寡助",
            china: "正义战争得道多助",
          },
        ],
        conclusion:
          "第一个特点决定日本的进攻能在中国横行一时、中国不能速胜；后三个特点决定中国不会亡国，经过长期抗战，最后胜利属于中国。",
      },
    },
    {
      id: "M1S2_N06",
      sceneId: "M1S2",
      order: 60,
      nodeType: "lecture",
      title: "持久战三阶段与力量对比变化",
      timeAnchor: "1938年5—6月",
      estimatedSeconds: 45,
      claimId: "M1S2_CLAIM_THREE_STAGES",
      claim: "抗日战争将经过战略防御、战略相持、战略反攻三个阶段，中国力量由劣势到平衡再到优势。",
      assetRefs: ["AST-M1S2-MAP-TABLE"],
      source: textbookSource("76—77", "96—97"),
      reviewState: "pending_review",
      render: {
        kind: "stage_flow",
        stages: [
          { id: "defense", order: 1, title: "战略防御", chinaPowerPosition: "劣势" },
          { id: "stalemate", order: 2, title: "战略相持", chinaPowerPosition: "平衡" },
          { id: "counteroffensive", order: 3, title: "战略反攻", chinaPowerPosition: "优势" },
        ],
        powerTrajectory: "劣势→平衡→优势",
        pivotStageId: "stalemate",
        pivotExplanation:
          "战略相持阶段的时间将相当长，遇到的困难也将最多，然而它是整个战争转变的枢纽。",
      },
    },
    {
      id: "M1S2_N07",
      sceneId: "M1S2",
      order: 70,
      nodeType: "lecture",
      title: "兵民是胜利之本",
      timeAnchor: "1938年5—6月",
      estimatedSeconds: 25,
      claimId: "M1S2_CLAIM_PEOPLES_WAR",
      claim: "动员和依靠群众、实行人民战争，是争取抗战胜利的唯一正确道路。",
      assetRefs: ["AST-M1S2-DOCUMENT-WALL"],
      source: textbookSource("77—78", "97—98"),
      reviewState: "pending_review",
      render: {
        kind: "narrative",
        paragraphs: [
          "《论持久战》强调：“兵民是胜利之本”，“战争的伟力之最深厚的根源，存在于民众之中”。",
          "《论持久战》系统阐明了党的抗日持久战战略总方针，是中国共产党领导抗日战争的纲领性文献。",
        ],
        emphasisTerms: ["兵民是胜利之本", "民众", "人民战争", "纲领性文献"],
      },
    },
  ],
  reviewState: "pending_review",
  reviewNotes: [
    {
      id: "M1S2_REVIEW_PAGE_REFERENCE",
      scope: "M1S2全部节点",
      priority: "before_frontend_release",
      state: "open",
      note: "原始DeepSeek方案把源PDF第95—98页标作“书中页码”；本蓝图仅纠正引用口径为书内印刷第75—78页 / 源PDF第95—98页，不改动原任务正文。",
    },
    {
      id: "M1S2_REVIEW_T02_WORDING",
      scope: "T02",
      priority: "before_formal_release",
      state: "open",
      note: "原任务2题干写作“洛川会议确定红军的基本作战方针”，教材正文表述为会议强调“在敌人后方放手发动独立自主的山地游击战争”。本次保持题干、选项和答案不变，发布前交党史/思政学科审核决定是否需要老板确认修订。",
    },
  ],
} satisfies M1S2Blueprint;
